import { ApolloClient, InMemoryCache, createHttpLink, from, split } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { createClient } from 'graphql-ws'
import { getMainDefinition } from '@apollo/client/utilities'
import { wsManager } from './websocket-manager'

// Helper function to get auth token
const getAuthToken = (): string | null => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('x11-auth-token')
  }
  return null
}

const httpLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_ENDPOINT || 'http://localhost:4000/graphql',
  credentials: 'include', // Send cookies with requests
})

// Track connection state
let wsReconnectAttempts = 0
let wsConnected = false
let wsClient: ReturnType<typeof createClient>
let wsConnectionPromise: Promise<void> | null = null
let wsConnectionResolve: (() => void) | null = null

// Create WebSocket client
wsClient = createClient({
  url: (import.meta.env.VITE_GRAPHQL_ENDPOINT || 'http://localhost:4000/graphql').replace(/^http/, 'ws'),
  connectionParams: () => {
    const token = getAuthToken()
    return {
      authToken: token
    }
  },
  // Enable automatic reconnection
  shouldRetry: () => true,
  retryAttempts: Infinity,
  // Implement exponential backoff for reconnection
  retryWait: async (retries) => {
    wsReconnectAttempts = retries
    // Start with 1 second, double each time, max 30 seconds
    // For iOS PWA, use shorter delays
    const baseDelay = wsManager.getIsIOSPWA() ? 500 : 1000
    const maxDelay = wsManager.getIsIOSPWA() ? 10000 : 30000
    const delay = Math.min(baseDelay * Math.pow(2, retries), maxDelay)
    
    // Only log after a few attempts to reduce noise
    if (retries > 2) {
      console.log(`WebSocket reconnecting in ${delay}ms (attempt ${retries + 1})${wsManager.getIsIOSPWA() ? ' [iOS PWA]' : ''}`)
    }
    
    // Dispatch error event after multiple failures
    if (retries >= 5) {
      window.dispatchEvent(new CustomEvent('wsConnectionError'))
    }
    
    await new Promise(resolve => setTimeout(resolve, delay))
  },
    on: {
      connected: () => {
        // Only log and dispatch if this is a reconnection, not initial connection
        if (wsReconnectAttempts > 0 || wsConnected) {
          console.log('WebSocket reconnected')
          window.dispatchEvent(new CustomEvent('wsReconnected'))
        } else {
          console.log('WebSocket initial connection established')
          window.dispatchEvent(new CustomEvent('wsInitialConnection'))
        }
        wsConnected = true
        wsReconnectAttempts = 0
        
        // Resolve the connection promise if it exists
        if (wsConnectionResolve) {
          wsConnectionResolve()
          wsConnectionResolve = null
          wsConnectionPromise = null
        }
      },
      error: (error) => {
        // Only log errors after a few attempts
        if (wsReconnectAttempts > 2) {
          console.error('WebSocket error:', error)
        }
      },
      closed: (event) => {
        // Only log if it wasn't a normal closure and we've had a few attempts
        if (event && typeof event === 'object' && 'code' in event && event.code !== 1000 && wsReconnectAttempts > 2) {
          console.warn('WebSocket closed unexpectedly, will attempt to reconnect')
        }
      },
      ping: () => {}, // Remove debug logging for ping/pong
      pong: () => {}
    },
    // Keep connection alive with ping/pong
    keepAlive: 15000, // Send ping every 15 seconds
    // Handle connection lifecycle
    lazy: false, // Connect immediately
    onNonLazyError: (error) => {
      // Only log after multiple attempts
      if (wsReconnectAttempts > 2) {
        console.error('WebSocket connection error:', error)
      }
    }
  })

// Create WebSocket link
const wsLink = new GraphQLWsLink(wsClient)

// iOS PWA specific event handlers
if (typeof window !== 'undefined') {
  // Force reconnect on iOS PWA events
  window.addEventListener('iosPWAResumed', () => {
    if (wsClient) {
      console.log('[Apollo] iOS PWA resumed, checking WebSocket connection')
      // The client will automatically reconnect if needed
    }
  })
  
  window.addEventListener('iosPWAFocused', () => {
    if (wsClient) {
      console.log('[Apollo] iOS PWA focused, ensuring WebSocket is active')
    }
  })
  
  window.addEventListener('wsForceReconnect', () => {
    if (wsClient) {
      console.log('[Apollo] Force reconnecting WebSocket')
      wsClient.terminate()
    }
  })
}

const authLink = setContext((_, { headers }) => {
  // Get the authentication token from localStorage if it exists
  // This is for backward compatibility and for extensions
  // Cookies will be sent automatically with credentials: 'include'
  let token: string | null = null

  if (typeof window !== 'undefined') {
    token = localStorage.getItem('x11-auth-token')
  }

  return {
    headers: {
      ...headers,
      // Set authorization header if we have a localStorage token (extensions)
      // For web app, backend will also check httpOnly cookies automatically
      ...(token && { authorization: `Bearer ${token}` }),
    }
  }
})

const errorLink = onError(({ graphQLErrors, networkError, response, operation }) => {
  // The demo reached something only a real X account can do.
  // Only for something the guest did. Background queries the demo refuses
  // (voice call numbers, signed URLs) fail quietly instead.
  const isMutation = operation.query.definitions.some(
    definition => definition.kind === 'OperationDefinition' && definition.operation === 'mutation',
  )
  if (isMutation && graphQLErrors?.some(error => (error as any).code === 'SIGN_IN_REQUIRED' || error.extensions?.code === 'SIGN_IN_REQUIRED')) {
    window.dispatchEvent(new CustomEvent('x11:sign-in-required', { detail: { reason: 'post' } }))
  }
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      // Handle JSONObject boolean value error
      if (message.includes('JSONObject cannot represent non-object value')) {
        console.warn(
          `[GraphQL warning]: Legacy boolean value in toolCall result. Path: ${path}. This will be auto-fixed.`
        )

        // Try to fix the response data if possible
        if (response && response.data) {
          try {
            // Navigate to the error path and fix boolean values
            const pathArray = path as string[]
            let current: Record<string, unknown> = response.data as Record<string, unknown>

            for (let i = 0; i < pathArray.length - 1; i++) {
              const key = pathArray[i]
              if (current && typeof current === 'object' && key in current) {
                const next = current[key]
                if (next && typeof next === 'object') {
                  current = next as Record<string, unknown>
                } else {
                  break
                }
              }
            }

            // Check if the final value is a boolean
            const lastKey = pathArray[pathArray.length - 1]
            if (current && typeof current === 'object' && lastKey in current && typeof current[lastKey] === 'boolean') {
              // Convert boolean to object
              current[lastKey] = {
                success: current[lastKey],
                message: 'Operation completed'
              }
            }
          } catch (e) {
            console.error('Failed to fix boolean value in response:', e)
          }
        }

        return // Don't log the full error for this known issue
      }

      // Check for token expiration errors
      if (message.includes('Invalid access token') || message.includes('jwt expired')) {
        console.warn('⚠️ Token expired, attempting refresh...')
        // Dispatch event to trigger token refresh
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('authTokenExpired'))
        }
        return
      }

      // A first-time visitor has no login: the startup probe for a login
      // cookie and the "who am I" query are expected to be refused.
      const visitorProbe =
        (operation.operationName === 'Me' || operation.operationName === 'RefreshToken') &&
        !localStorage.getItem('x11-auth-token') &&
        /Access denied|No refresh token/i.test(message)
      if (visitorProbe) return

      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      )
    })
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError}`)

    // Handle authentication errors
    if ('statusCode' in networkError && networkError.statusCode === 401) {
      // Don't immediately redirect - try token refresh first
      if (typeof window !== 'undefined') {
        // Dispatch event to try refresh
        window.dispatchEvent(new CustomEvent('authTokenExpired'))

        // If this is not a refresh operation, wait a bit for refresh to complete
        if (operation.operationName !== 'RefreshToken') {
          // Set a timeout to redirect if refresh doesn't work
          setTimeout(() => {
            const token = localStorage.getItem('x11-auth-token')
            if (!token) {
              console.log('❌ Token refresh failed, redirecting to login...')
              // Clear all tokens
              localStorage.removeItem('x11-auth-token')
              localStorage.removeItem('x11-refresh-token')
              document.cookie = 'x11-auth-token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;'
              document.cookie = 'x11-refresh-token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;'
              window.location.href = '/'
            }
          }, 2000)
        } else {
          // Refresh itself failed, redirect immediately
          console.log('❌ Refresh token invalid, redirecting to login...')
          localStorage.removeItem('x11-auth-token')
          localStorage.removeItem('x11-refresh-token')
          document.cookie = 'x11-auth-token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;'
          document.cookie = 'x11-refresh-token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;'
          window.location.href = '/'
        }
      }
    }
  }
})

// Split link - use WebSocket for subscriptions, HTTP for everything else
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    )
  },
  wsLink,
  httpLink
)

export const apolloClient = new ApolloClient({
  link: from([errorLink, authLink, splitLink]),
  cache: new InMemoryCache({
    typePolicies: {
      User: {
        fields: {
          subscription: {
            merge(_, incoming) {
              return incoming
            }
          }
        }
      }
    }
  }),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    },
    query: {
      errorPolicy: 'all',
    },
  },
})

// Token management utilities
export const setAuthToken = (token: string, refreshToken?: string) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('x11-auth-token', token)
    if (refreshToken) {
      localStorage.setItem('x11-refresh-token', refreshToken)
    }
    
    // Dispatch a custom event to notify about token changes
    window.dispatchEvent(new CustomEvent('authTokenChanged'))
  }
}

export const clearAuthTokens = () => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('x11-auth-token')
    localStorage.removeItem('x11-refresh-token')
    // Clear cookies by setting them to expire
    document.cookie = 'x11-auth-token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;'
    document.cookie = 'x11-refresh-token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;'
    
    // Dispatch a custom event to notify about token changes
    window.dispatchEvent(new CustomEvent('authTokenChanged'))
  }
}

// Export the getAuthToken function
export { getAuthToken }

// Function to wait for WebSocket connection
export const waitForWebSocketConnection = (): Promise<void> => {
  // If already connected, resolve immediately
  if (wsConnected) {
    return Promise.resolve()
  }
  
  // If there's already a connection promise, return it
  if (wsConnectionPromise) {
    return wsConnectionPromise
  }
  
  // Create a new connection promise
  wsConnectionPromise = new Promise<void>((resolve) => {
    wsConnectionResolve = resolve
    
    // Also add a timeout to avoid waiting forever
    const timeout = setTimeout(() => {
      if (wsConnectionResolve === resolve) {
        console.warn('WebSocket connection timeout after 5 seconds')
        resolve() // Resolve anyway to avoid blocking
        wsConnectionResolve = null
        wsConnectionPromise = null
      }
    }, 5000)
    
    // Clear timeout if we connect successfully
    const originalResolve = resolve
    wsConnectionResolve = () => {
      clearTimeout(timeout)
      originalResolve()
    }
  })
  
  return wsConnectionPromise
}

// Check if WebSocket is connected
export const isWebSocketConnected = () => wsConnected

export const getRefreshToken = (): string | null => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('x11-refresh-token')
  }
  return null
}

// Handle app visibility changes (important for mobile)
if (typeof window !== 'undefined') {
  let lastVisibilityChange = Date.now()
  
  // Handle page visibility changes
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      const now = Date.now()
      const timeSinceLastChange = now - lastVisibilityChange
      
      // If the page was hidden for more than 30 seconds, dispatch event
      if (timeSinceLastChange > 30000) {
        // The WebSocket client will automatically reconnect if needed
        // Dispatch event for components that might need to refresh
        window.dispatchEvent(new CustomEvent('appResumed'))
      }
      
      lastVisibilityChange = now
    }
  })
  
  // Also handle online/offline events
  window.addEventListener('online', () => {
    // WebSocket will reconnect automatically
  })
  
  window.addEventListener('offline', () => {
    // Connection will be retried automatically
  })
  
  // Listen for auth token changes and reconnect WebSocket
  window.addEventListener('authTokenChanged', () => {
    console.log('Auth token changed, reconnecting WebSocket with new token')
    if (wsClient) {
      wsClient.terminate() // Force disconnect
      // Client will automatically reconnect with new token from connectionParams
    }
  })
}